import { useEffect, useState } from "react";
import api from "../api/client";
import { toast } from "sonner";
import { Send, Loader2, Save } from "lucide-react";
import logger from "../lib/logger";

function ClientRow({ client, onSaved }) {
  const [email, setEmail] = useState(client.client_email || "");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/reports/clients/email", {
        client_name: client.client_name,
        client_email: email.trim() || null,
      });
      toast.success(email.trim() ? `Email saved for ${client.client_name}` : "Email cleared");
      onSaved();
    } catch (err) {
      logger.error(err);
      toast.error(err?.response?.data?.detail || "Failed to save email");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports/client-report/send-now", null, {
        params: { client: client.client_name },
      });
      const r = data?.results?.[0];
      if (r?.sent) {
        toast.success(`Weekly report emailed to ${r.email}`);
      } else {
        toast.error(r?.reason === "no_email" ? "Set an email first" : r?.reason || "Send failed");
      }
    } catch (err) {
      logger.error(err);
      toast.error(err?.response?.data?.detail || "Failed to send report");
    } finally {
      setSending(false);
    }
  };

  const dirty = (email.trim() || "") !== (client.client_email || "");

  return (
    <div
      className="flex items-center gap-2 py-2 border-b border-gray-100 flex-wrap"
      data-testid={`client-report-row-${client.client_name}`}
    >
      <div className="flex-1 min-w-[140px]">
        <div className="text-sm font-semibold">{client.client_name}</div>
        <div className="text-[11px] text-gray-500">
          {client.site_count} site{client.site_count > 1 ? "s" : ""}
        </div>
      </div>
      <input
        type="email"
        placeholder="client@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        data-testid={`client-email-input-${client.client_name}`}
        className="border border-gray-300 px-2 py-1.5 rounded-sm text-xs w-56"
      />
      <button
        onClick={save}
        disabled={saving || !dirty}
        data-testid={`save-client-email-${client.client_name}`}
        className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 text-xs font-bold rounded-sm hover:bg-gray-100 disabled:opacity-40"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
      </button>
      <button
        onClick={sendNow}
        disabled={sending || !client.client_email}
        title={client.client_email ? "Email this client their weekly report now" : "Save an email first"}
        data-testid={`send-client-report-${client.client_name}`}
        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#002FA7] text-white text-xs font-bold rounded-sm hover:bg-[#002277] disabled:opacity-40"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send now
      </button>
    </div>
  );
}

export default function ClientReportsCard() {
  const [clients, setClients] = useState([]);
  const [sendingAll, setSendingAll] = useState(false);

  const load = () =>
    api.get("/reports/clients").then(({ data }) => setClients(data || [])).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const sendAll = async () => {
    setSendingAll(true);
    try {
      const { data } = await api.post("/reports/client-report/send-now");
      toast.success(`Sent ${data.sent} client report${data.sent === 1 ? "" : "s"}${data.skipped ? ` — ${data.skipped} skipped (no email)` : ""}`);
    } catch (err) {
      logger.error(err);
      toast.error(err?.response?.data?.detail || "Failed to send client reports");
    } finally {
      setSendingAll(false);
    }
  };

  const withEmail = clients.filter((c) => c.client_email).length;

  return (
    <section
      data-testid="client-reports-card"
      className="border border-gray-300 bg-white p-5 rounded-sm"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-gray-500">Automated email</div>
          <h3 className="text-xl font-black tracking-tight mt-1">Weekly client reports</h3>
          <p className="text-sm text-gray-600 mt-2">
            Every <strong>Monday 09:00 UTC</strong>, each client below automatically receives an
            email with a branded PDF + Excel report covering <strong>only their sites</strong>.
            Set each client's email here, or send on demand.
          </p>
        </div>
        <button
          onClick={sendAll}
          disabled={sendingAll || withEmail === 0}
          data-testid="send-all-client-reports-button"
          className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#002FA7] text-[#002FA7] font-bold tracking-wide hover:bg-[#002FA7] hover:text-white disabled:opacity-50 rounded-sm transition"
        >
          {sendingAll ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Sending…
            </>
          ) : (
            <>
              <Send className="w-4 h-4" /> Send all now ({withEmail})
            </>
          )}
        </button>
      </div>

      <div className="mt-4 border-t border-gray-200 pt-2">
        {clients.length === 0 ? (
          <p className="text-sm text-gray-500 py-3">
            No clients yet — assign a client name to sites in the <strong>Sites</strong> tab.
          </p>
        ) : (
          clients.map((c) => <ClientRow key={c.client_name} client={c} onSaved={load} />)
        )}
      </div>
    </section>
  );
}
